import React, { useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Icon, IconButton, Menu, MenuItem } from '@material-ui/core';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { capitalizeAll, useDropdown, useQueryParams } from '@moderepo/mode-app-components';
import { getLogTypeName, LogType } from '@moderepo/mode-app-components-internal';
import { useRitzTranslation } from '../../utils/customHooks';
import { UserMessageLogsContainer } from './UserMessageLogsContainer';
import { UserHttpLogsContainer } from './UserHttpLogsContainer';
import { RouteHelper } from '../routes/RouteHelper';


/**
 * This is the main page which we show logs, Message log, http request logs, and log requests.
 */
export const UserLogsPage: React.FC = () => {
    const params = useParams();
    const navigate = useNavigate();

    // Get params from route/url
    const projectId = Number(params.projectId);
    const userId = Number(params.userId);

    const { state:dataState } = useContext(ritzDataContext);
    const project = selectProjectById(dataState, projectId);
    const { trans } = useRitzTranslation(project);
    const queryParams = useQueryParams();
    const queryLogType = queryParams.get('logType');
    const logType = queryLogType === LogType.MESSAGES || queryLogType === LogType.HTTP_REQUESTS || queryLogType === LogType.LOG_REQUESTS
        ? queryLogType as LogType : LogType.MESSAGES;
    const routeHelper = RouteHelper.getInstance(navigate);
    const [menuAnchorEl, isMenuOpened, onAnchorElClicked, onMenuClosed] = useDropdown();



    const onMenuItemClicked = useCallback((type: LogType) => {
        routeHelper.gotoUserLogsPage({ projectId, userId, logType: type });
    }, [routeHelper, projectId, userId]);


    return (
        <div className="page-main-content">
            <div className="title-bar">
                <div className="title-bar-item title-text">
                    <div className="page-title">
                        {trans('page.title.user_logs')}
                    </div>
                    <div className="page-subtitle">
                        {capitalizeAll(trans('page.subtitle.user_logs', { log_type: trans(getLogTypeName(logType)) }))}
                    </div>
                </div>

                <IconButton onClick={onAnchorElClicked}>
                    <Icon>more_vert</Icon>
                </IconButton>
                <Menu
                    getContentAnchorEl={null}
                    anchorEl={menuAnchorEl}
                    anchorOrigin={{
                        vertical  : 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical  : 'top',
                        horizontal: 'right',
                    }}
                    keepMounted
                    open={isMenuOpened}
                    onClose={onMenuClosed}
                >
                    <MenuItem
                        disabled={logType === LogType.MESSAGES}
                        onClick={() => {
                            onMenuClosed();
                            onMenuItemClicked(LogType.MESSAGES);
                        }}
                    >
                        {capitalizeAll(trans('label.view_message_logs'))}
                    </MenuItem>
                    <MenuItem
                        disabled={logType === LogType.HTTP_REQUESTS}
                        onClick={() => {
                            onMenuClosed();
                            onMenuItemClicked(LogType.HTTP_REQUESTS);
                        }}
                    >
                        {capitalizeAll(trans('label.view_http_request_logs'))}
                    </MenuItem>
                </Menu>
            </div>

            <>
                {logType === LogType.MESSAGES && (
                    <UserMessageLogsContainer projectId={projectId} userId={userId} />
                )}

                {logType === LogType.HTTP_REQUESTS && (
                    <UserHttpLogsContainer projectId={projectId} userId={userId} />
                )}
            </>
        </div>
    );
};
